import { useEffect, useState } from 'react';
import facebookAvatar from '@/assets/images/avatar-fb.png';
import instagramAvatar from '@/assets/images/avatar-ig.png';
import linkedinAvatar from '@/assets/images/avatar-linkedin.png';
import useWorkflowStore from '@/stores/WorkflowStore';
import { format } from 'date-fns';
import { ChevronLeft, ChevronRight, History, Loader2 } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';

import { cn } from '@/lib/utils';
import { fetchWorkflowInstances } from '@/hooks/workflow';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const WorkflowRunsPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  // Reset current page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, fromDate, toDate]);

  const { workflowHistoryList, setWorkflowHistoryList } = useWorkflowStore(
    useShallow((state) => ({
      workflowHistoryList: state.workflowHistoryList,
      setWorkflowHistoryList: state.setWorkflowHistoryList,
    }))
  );

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const params = {
          ...(statusFilter !== 'all' && { status: statusFilter }),
          ...(fromDate && { from_date: format(fromDate, 'yyyy-MM-dd') }),
          ...(toDate && { to_date: format(toDate, 'yyyy-MM-dd') }),
          page: currentPage,
          per_page: rowsPerPage,
        };

        const response = await fetchWorkflowInstances(params);
        setWorkflowHistoryList(response.data);
      } catch (error) {
        console.error('Error fetching workflow instances:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [statusFilter, fromDate, toDate, currentPage, rowsPerPage]);

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

    if (diffInHours < 24) {
      return `${diffInHours} hours ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays} days ago`;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return format(new Date(dateString), 'MMM d, yyyy h:mm a');
  };

  const getStepStatus = (step) => {
    if (step.execution?.failed_at) return 'Failed';
    if (step.execution?.completed_at) return 'Completed';
    if (step.execution?.started_at) return 'Running';
    if (step.execution?.queued_at) return 'Queued';
    return 'Skipped';
  };

  const renderChannels = (channels) => {
    if (!channels || channels.length === 0)
      return <span className='text-muted-foreground'>No channels</span>;

    return (
      <div className='flex gap-2'>
        {channels.map((channel) => (
          <div key={channel} className='flex items-center justify-center'>
            {channel.toLowerCase() === 'instagram' ? (
              <img src={instagramAvatar} className='h-4 w-4 ' alt='Instagram' />
            ) : channel.toLowerCase() === 'facebook' ? (
              <img src={facebookAvatar} className='h-4 w-4 ' alt='Facebook' />
            ) : channel.toLowerCase() === 'linkedin' ? (
              <img src={linkedinAvatar} className='h-4 w-4 ' alt='LinkedIn' />
            ) : null}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className='h-full'>
      <div className='flex flex-col gap-4'>
        <div className='flex items-center gap-3  pt-6'>
          <div className='rounded-md bg-blue-100 p-2 text-blue-600 dark:bg-blue-950 dark:text-blue-400'>
            <History className='h-6 w-6' />
          </div>
          <div>
            <h1 className='text-2xl font-bold tracking-tight'>Workflow Runs</h1>
            <p className='text-muted-foreground'>
              View and monitor your workflow execution history
            </p>
          </div>
        </div>

        <div className=' rounded-lg border p-4 dark:border-slate-800'>
          <div className='flex flex-col gap-4 pb-4'>
            <div className='flex items-center justify-between gap-4'>
              <div className='flex items-center gap-4'>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className='w-[180px] dark:border-slate-800 dark:bg-slate-950'>
                    <SelectValue placeholder='All Status' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='all'>All Status</SelectItem>
                    <SelectItem value='running'>Running</SelectItem>
                    <SelectItem value='deferred'>In Progress</SelectItem>
                    <SelectItem value='completed'>Completed</SelectItem>
                    <SelectItem value='failed'>Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <Accordion type='single' collapsible className='w-full'>
            <div className='mb-4 border-b dark:border-slate-800'>
              <div className='flex items-center py-3 text-sm font-medium text-muted-foreground'>
                <div className='w-[120px] px-2'>Last update</div>
                <div className='w-[130px] px-2'>Run Status</div>
                <div className='w-[350px] px-2'>Workflow</div>
                <div className='w-[200px] px-2 text-center'>Contact</div>
                <div className='w-[120px] px-2'>Channel</div>
                <div className='w-[80px] px-2 text-center'>Steps</div>
              </div>
            </div>

            {isLoading ? (
              <div className='flex h-32 items-center justify-center'>
                <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
              </div>
            ) : workflowHistoryList?.data?.length === 0 ? (
              <div className='flex h-32 items-center justify-center'>
                <div className='text-center text-muted-foreground'>
                  <p className='text-lg font-medium'>No workflow runs found</p>
                  <p className='text-sm'>
                    Try adjusting your filters or check back later
                  </p>
                </div>
              </div>
            ) : (
              workflowHistoryList?.data?.map((run) => (
                <AccordionItem
                  key={run._id}
                  value={run._id}
                  className='border-b-0'
                >
                  <AccordionTrigger className='h-auto p-0 hover:no-underline'>
                    <div className='flex items-center py-2'>
                      <div className='w-[120px] px-2 text-muted-foreground'>
                        {formatTimeAgo(run.started_at)}
                      </div>
                      <div className='w-[130px] px-2'>
                        <div className='flex items-center gap-2'>
                          <Badge
                            variant='secondary'
                            className={cn(
                              'capitalize',
                              run.status === 'completed' &&
                                'bg-green-500/10 text-green-500 hover:bg-green-500/20',
                              run.status === 'failed' &&
                                'bg-red-500/10 text-red-500 hover:bg-red-500/20',
                              run.status === 'running' &&
                                'bg-yellow-500/10 text-yellow-500 hover:bg-yellow-500/20',
                              run.status === 'queued' &&
                                'bg-blue-500/10 text-blue-500 hover:bg-blue-500/20',
                              run.status === 'deferred' &&
                                'bg-orange-500/10 text-orange-500 hover:bg-orange-500/20'
                            )}
                          >
                            {run.status === 'deferred'
                              ? 'In Progress'
                              : run.status}
                          </Badge>
                        </div>
                      </div>

                      <div className='w-[350px] truncate px-2 text-start'>
                        {run.workflow_name}
                      </div>
                      <div className='w-[200px] truncate px-2'>
                        {run.contact ? (
                          <a
                            href={`/inbox/${run.contact._id}`}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-primary hover:text-primary/80'
                          >
                            {run.contact.full_name
                              ? run.contact.full_name
                              : '<Unnamed Contact>'}
                          </a>
                        ) : (
                          'N/A'
                        )}
                      </div>
                      <div className='flex w-[120px] px-2'>
                        {renderChannels(run.channel)}
                      </div>
                      <div className='w-[80px] px-2 text-center'>
                        {run.steps?.length || 0}
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className='space-y-4 px-2 py-4'>
                      <div>
                        <Table className='border dark:border-slate-800'>
                          <TableHeader>
                            <TableRow className='hover:bg-transparent dark:border-slate-800 dark:hover:bg-transparent'>
                              <TableHead className='w-[60px]'>Step</TableHead>
                              <TableHead className='w-[200px]'>
                                Action
                              </TableHead>
                              <TableHead className='w-[120px]'>
                                Status
                              </TableHead>
                              <TableHead className='w-[180px]'>
                                Timeline
                              </TableHead>
                              <TableHead>Details</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {run.steps?.map((step) => (
                              <TableRow
                                key={step.id}
                                className='dark:border-slate-800'
                              >
                                <TableCell>{step.order}</TableCell>
                                <TableCell>
                                  <div className='flex flex-col gap-1'>
                                    <div className='flex items-center gap-2'>
                                      <span className='font-medium capitalize'>
                                        {step.type}
                                      </span>
                                      {step.action_type && (
                                        <Badge
                                          variant='secondary'
                                          className='text-xs'
                                        >
                                          {step.action_type.replace(/_/g, ' ')}
                                        </Badge>
                                      )}
                                    </div>
                                    {step.delay && (
                                      <div className='text-xs text-muted-foreground'>
                                        Delay: {step.delay}{' '}
                                        {step.delay_unit === 'seconds'
                                          ? 'secs'
                                          : step.delay_unit === 'minutes'
                                            ? 'mins'
                                            : step.delay_unit === 'hours'
                                              ? 'hrs'
                                              : step.delay_unit}
                                      </div>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className='flex items-center gap-2'>
                                    <Badge
                                      variant='secondary'
                                      className={cn(
                                        'cursor-default capitalize',
                                        getStepStatus(step) === 'Completed' &&
                                          'bg-green-500/10 text-green-500 hover:bg-green-500/20',
                                        getStepStatus(step) === 'Failed' &&
                                          'bg-red-500/10 text-red-500 hover:bg-red-500/20',
                                        getStepStatus(step) === 'Running' &&
                                          'bg-yellow-500/10 text-yellow-500 hover:bg-yellow-500/20',
                                        getStepStatus(step) === 'Queued' &&
                                          'bg-blue-500/10 text-blue-500 hover:bg-blue-500/20',
                                        getStepStatus(step) === 'Skipped' &&
                                          'bg-gray-500/10 text-gray-500 hover:bg-gray-500/20'
                                      )}
                                    >
                                      {getStepStatus(step) === 'Skipped'
                                        ? 'Yet to Start'
                                        : getStepStatus(step)}
                                    </Badge>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className='flex flex-col gap-1 text-xs text-muted-foreground'>
                                    {step.execution?.started_at && (
                                      <div>
                                        Started:{' '}
                                        {formatDateTime(
                                          step.execution.started_at
                                        )}
                                      </div>
                                    )}
                                    {step.execution?.completed_at && (
                                      <div>
                                        Completed:{' '}
                                        {formatDateTime(
                                          step.execution.completed_at
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {step.execution?.details && (
                                    <div className='text-sm text-muted-foreground'>
                                      {step.execution.details}
                                    </div>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))
            )}
          </Accordion>

          <div className='mt-4 border-t dark:border-slate-800'>
            <div className='flex items-center justify-between px-2 py-4'>
              <div className='flex items-center gap-4'>
                <div className='flex items-center gap-2 text-sm'>
                  <span className='text-muted-foreground'>Rows per page</span>
                  <Select
                    value={rowsPerPage.toString()}
                    onValueChange={(value) => setRowsPerPage(Number(value))}
                  >
                    <SelectTrigger className='h-8 w-[70px] border-0 bg-transparent'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='10'>10</SelectItem>
                      <SelectItem value='20'>20</SelectItem>
                      <SelectItem value='50'>50</SelectItem>
                      <SelectItem value='100'>100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className='text-sm text-muted-foreground'>
                  {workflowHistoryList.from || 0}-{workflowHistoryList.to || 0}{' '}
                  of {workflowHistoryList.total || 0} items
                </div>
              </div>

              <div className='flex items-center gap-2'>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={currentPage === 1}
                  className='h-8 w-8 p-0'
                >
                  <ChevronLeft className='h-4 w-4' />
                  <span className='sr-only'>Previous page</span>
                </Button>

                <div className='flex items-center'>
                  <Input
                    type='number'
                    min={1}
                    max={workflowHistoryList.last_page || 1}
                    value={currentPage}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (
                        value >= 1 &&
                        value <= (workflowHistoryList.last_page || 1)
                      ) {
                        setCurrentPage(value);
                      }
                    }}
                    className='h-8 w-12 border-0 bg-transparent text-center'
                  />
                </div>

                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() =>
                    setCurrentPage((prev) =>
                      Math.min(workflowHistoryList.last_page || 1, prev + 1)
                    )
                  }
                  disabled={
                    currentPage === (workflowHistoryList.last_page || 1)
                  }
                  className='h-8 w-8 p-0'
                >
                  <ChevronRight className='h-4 w-4' />
                  <span className='sr-only'>Next page</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowRunsPage;
